import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

import rawMaterialsService from '../../service/api/RawMaterialsService';
import { Dialog } from 'primereact/dialog';
import RawMaterialDialog from './RawMaterialDialog';
import { Trans, useTranslation } from 'react-i18next';
import { RawMaterial } from '../../types/RawMaterial';

import { Toast } from 'primereact/toast';
import { SETTINGS } from '../../enums/settings.enum';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import { Chip } from 'primereact/chip';
import groupsService from '../../service/api/GroupsService';
import { GroupConnectors } from '../../types/GroupConnectors';

export const RawMaterialManagement = () => {
  const toast = useRef<any>();

  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>(null);
  const [rawMaterialToDelete, setRawMaterialToDelete] = useState<RawMaterial>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<RawMaterial>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);

  const [connectors, setConnectors] = useState<GroupConnectors[]>([]);

  const { t } = useTranslation();

  const showSaveToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('rawMaterials.toast_saved'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const showAddToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('rawMaterials.toast_added'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const showDeleteToast = (summary: string) => {
    toast.current.show({
      severity: 'info',
      summary: summary,
      detail: t('rawMaterials.toast_deleted'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const loadData = () => {
    setLoading(true);
    Promise.all([rawMaterialsService.getAll(), groupsService.getAll()]).then((response) => {
      const _rawMaterials = response[0];
      _rawMaterials.forEach((_x) => {});
      setRawMaterials(_rawMaterials);

      const _groups = response[1];

      _groups.forEach((g) => {
        g.connectors.forEach((c) => {
          c.groupTemplateConnectorName = `${g.name}: ${c.groupTemplateConnectorName}`;
        });
      });
      setConnectors(
        _groups.flatMap((g) =>
          g.connectors.filter(
            (c) => c.groupTemplateConnectorType === 'OUTPUT' && c.groupTemplateConnectorFunction === 'STANDARD'
          )
        )
      );

      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const rawMaterialsTableHeader = (
    <div className="table-header">
      {t('rawMaterials.header')}
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addRawMaterial();
          }}
        ></Button>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    loadData();
  };

  const confirmDeleteRawMaterial = (rawMaterial: RawMaterial) => {
    setRawMaterialToDelete(rawMaterial);
    setDeleteDialogVisible(true);
  };

  const deleteRawMaterial = () => {
    setLoading(true);
    rawMaterialsService.delete(rawMaterialToDelete.id).then(() => {
      loadData();

      showDeleteToast(rawMaterialToDelete.name);
    });

    setDeleteDialogVisible(false);
  };
  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editRawMaterial = (_rawMaterial: RawMaterial) => {
    setSelectedRowToEdit(_rawMaterial);
    setAddEditDialogVisible(true);
  };

  const addRawMaterial = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => deleteRawMaterial()}
      />
    </>
  );

  const bodyTemplate = (data: RawMaterial, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const chipsTemplate = (data: RawMaterial, props: ColumnBodyOptions) => {
    return (
      <>
        {data[props.field]?.map((item: any) => {
          const connector = connectors.find((c) => c.id === item);
          return connector ? (
            <Chip key={connector.id} label={connector.groupTemplateConnectorName} className="mr-2 mb-2" />
          ) : null;
        })}
      </>
    );
  };

  const actionTemplate = (data: RawMaterial) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editRawMaterial(data);
        }}
      ></Button>
      <WriteOnlyButton
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        onClick={() => {
          confirmDeleteRawMaterial(data);
        }}
      />
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={rawMaterials}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('rawMaterials.empty_message')}
            loading={loading}
            header={rawMaterialsTableHeader}
          >
            <Column field="name" header={t('common.name')} sortable body={bodyTemplate}></Column>
            <Column field="connectorIds" header={t('rawMaterials.connectors')} body={chipsTemplate}></Column>

            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <RawMaterialDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            rawMaterial={selectedRowToEdit}
            showAddToast={showAddToast}
            showSaveToast={showSaveToast}
            availableConnectors={connectors}
          />
          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {rawMaterialToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: rawMaterialToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
